import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import type {AdminAfriexPerson} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';

type ImageType = {
  id: string;
  source: string;
  url: string;
};

export const useKYCReceipt = (data: AdminAfriexPerson) => {
  const kycImages = data.kyc?.images;
  const mediaImages = data.kyc?.media?.images;
  const docImages = data.kyc?.docs;
  const [imagesLoading, setImagesLoading] = useState(true);
  const [images, setImages] = useState<ImageType[]>([]);
  const kycImagesStr = kycImages ? JSON.stringify(kycImages) : '';
  const mediaImagesStr = mediaImages?.join(',') || '';
  const docImagesStr = docImages ? JSON.stringify(docImages) : '';

  useEffect(() => {
    if (!data?.id) {
      return;
    }

    const fetchImages = () => {
      setImagesLoading(true);

      try {
        const updates: {id: string; source: string; url: string}[] = [];

        // KYC images (object)
        if (kycImages) {
          Object.entries(kycImages).forEach(([key, value]) => {
            if (value) {
              updates.push({
                id: `kyc-${key}`,
                source: 'kyc',
                url: value,
              });
            }
          });
        }

        // Media images (array)
        if (mediaImages?.length) {
          mediaImages.forEach((url, index) => {
            if (url) {
              updates.push({
                id: `media-${index}`,
                source: 'media',
                url,
              });
            }
          });
        }

        // Doc images (array of objects)
        if (docImages?.length) {
          docImages.forEach((doc, index) => {
            const url = doc.media?.source;
            if (url) {
              updates.push({
                id: `doc-${index}`,
                source: 'doc',
                url,
              });
            }
          });
        }

        setImages((prev) => {
          const map = new Map(prev.map((item) => [item.id, item]));

          updates.forEach((update) => {
            map.set(update.id, {
              ...map.get(update.id),
              ...update,
            });
          });

          return Array.from(map.values());
        });
      } catch (err) {
        console.error('CAUGHT ERROR:', err);
        toast.error(getResponseMessage(err) || 'Error fetching images');
      } finally {
        setImagesLoading(false);
      }
    };

    // fetchImages is synchronous and try/catches internally (clears
    // `imagesLoading`, reports through toast.error), so nothing here can throw.
    fetchImages();
  }, [data?.id, kycImagesStr, mediaImagesStr, docImagesStr]);

  return {imagesLoading, images, setImages};
};
